import { useEffect, useMemo, useState } from 'react'
import type { CdphConfigResponse, TestType } from '@alpha-edge/shared'
import { CDPH_ENDPOINTS } from '@alpha-edge/shared'
import { PageHeader, FormField, Alert, btnPrimary, colors, input, card } from '../../components/ui'
import { api, ApiError } from '../../services/api'

// §7.6.23 Slice 4 -- CDPH SaPHIRE admin config page. Mirrors legacy
// `CdphSaphireSetting.jsx`: an enable toggle gated by a client-side
// enable-guard (mirrored server-side by `cdphConfigSchema`'s superRefine),
// an mTLS PEM bundle uploaded all-three-or-none via FileReader, and a
// read-only reportable-tests summary (legacy `CdphReportableSummary.jsx`).

type CdphEnvironment = CdphConfigResponse['environment']

interface ConfigForm {
  enabled: boolean
  environment: CdphEnvironment
  endpointUrl: string
  submitterOid: string
  clia: string
  sendingFacility: string
}

const EMPTY_FORM: ConfigForm = {
  enabled: false,
  environment: 'stage',
  endpointUrl: CDPH_ENDPOINTS.stage,
  submitterOid: '',
  clia: '',
  sendingFacility: '',
}

function configToForm(c: CdphConfigResponse): ConfigForm {
  return {
    enabled: c.enabled,
    environment: c.environment,
    endpointUrl: c.endpointUrl ?? CDPH_ENDPOINTS[c.environment],
    submitterOid: c.submitterOid ?? '',
    clia: c.clia ?? '',
    sendingFacility: c.sendingFacility ?? '',
  }
}

interface CertSlot {
  readonly fileName: string | null
  readonly text: string | null
}

const EMPTY_CERT_SLOT: CertSlot = { fileName: null, text: null }

interface ReportableRow {
  readonly name: string
  readonly loinc: string
  readonly polarity: 'positive' | 'all'
}

function buildReportableRows(testTypes: readonly TestType[]): readonly ReportableRow[] {
  return testTypes
    .filter((t) => t.cdphConfig?.isReportable)
    .map((t) => ({
      name: t.name,
      loinc: t.cdphConfig?.loinc || t.loincCode || '',
      polarity: t.cdphConfig?.reportPolarity ?? 'positive',
    }))
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`
  return value
}

function downloadReportableCsv(rows: readonly ReportableRow[]): void {
  const header = ['Test Name', 'LOINC', 'Polarity']
  const lines = [header, ...rows.map((r) => [r.name, r.loinc, r.polarity === 'all' ? 'All results' : 'Positive only'])]
  const csv = lines.map((line) => line.map(csvEscape).join(',')).join('\r\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `cdph_reportable_tests-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export function CdphSettingsPage() {
  const [form, setForm] = useState<ConfigForm>(EMPTY_FORM)
  const [certOnFile, setCertOnFile] = useState(false)
  const [certUpdatedAt, setCertUpdatedAt] = useState<string | null>(null)
  const [certFiles, setCertFiles] = useState<{ key: CertSlot; cert: CertSlot; ca: CertSlot }>({
    key: EMPTY_CERT_SLOT,
    cert: EMPTY_CERT_SLOT,
    ca: EMPTY_CERT_SLOT,
  })
  const [testTypes, setTestTypes] = useState<readonly TestType[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .get<CdphConfigResponse>('/admin/cdph/config')
      .then((res) => {
        if (cancelled || !res.data) return
        setForm(configToForm(res.data))
        setCertOnFile(res.data.certOnFile)
        setCertUpdatedAt(res.data.certUpdatedAt)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load CDPH configuration')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Reportable-tests summary: the test-types list endpoint has no
  // isReportable filter, so we page through it (limit=100, the API's max)
  // and filter client-side. Fine at proportionate scale -- orgs run well
  // under a hundred configured test types.
  useEffect(() => {
    let cancelled = false
    api
      .get<readonly TestType[]>('/test-types?limit=100')
      .then((res) => {
        if (!cancelled && res.data) setTestTypes(res.data)
      })
      .catch(() => {
        // Non-fatal -- the summary just stays empty.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const reportableRows = useMemo(() => buildReportableRows(testTypes), [testTypes])

  const update = <K extends keyof ConfigForm>(key: K, value: ConfigForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const handleEnvironmentChange = (env: CdphEnvironment) => {
    setForm((prev) => ({ ...prev, environment: env, endpointUrl: CDPH_ENDPOINTS[env] }))
  }

  const readCertFile = (slot: 'key' | 'cert' | 'ca') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) {
      setCertFiles((prev) => ({ ...prev, [slot]: EMPTY_CERT_SLOT }))
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      setCertFiles((prev) => ({ ...prev, [slot]: { fileName: file.name, text } }))
    }
    reader.onerror = () => setError('Failed to read the selected certificate file.')
    reader.readAsText(file)
  }

  const certSlotsFilled = [certFiles.key, certFiles.cert, certFiles.ca].filter((s) => s.text !== null).length
  const certPartiallyUploaded = certSlotsFilled > 0 && certSlotsFilled < 3
  const certFullyUploaded = certSlotsFilled === 3

  // Client-side mirror of the server's enable-guard (`cdphConfigSchema`'s
  // superRefine): when enabling, the required fields must be present and a
  // cert must be on file (existing or freshly uploaded in this save).
  function validate(): string | null {
    if (certPartiallyUploaded) {
      return 'Upload all three certificate files together (client key, client certificate, CA chain).'
    }
    if (form.enabled) {
      if (!form.endpointUrl.trim()) return 'Endpoint URL is required when CDPH reporting is enabled.'
      if (!form.submitterOid.trim()) return 'Submitter OID is required when CDPH reporting is enabled.'
      if (!form.clia.trim()) return 'CLIA is required when CDPH reporting is enabled.'
      if (!form.sendingFacility.trim()) return 'Sending Facility is required when CDPH reporting is enabled.'
      if (!certOnFile && !certFullyUploaded) {
        return 'A certificate bundle must be on file when CDPH reporting is enabled.'
      }
    }
    return null
  }

  async function handleSave() {
    setError(null)
    setSuccess(null)
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        enabled: form.enabled,
        environment: form.environment,
        endpointUrl: form.endpointUrl || undefined,
        submitterOid: form.submitterOid || undefined,
        clia: form.clia || undefined,
        sendingFacility: form.sendingFacility || undefined,
      }
      if (certFullyUploaded) {
        payload.certBundle = { key: certFiles.key.text, cert: certFiles.cert.text, ca: certFiles.ca.text }
      }

      const res = await api.put<CdphConfigResponse>('/admin/cdph/config', payload)
      if (res.data) {
        setForm(configToForm(res.data))
        setCertOnFile(res.data.certOnFile)
        setCertUpdatedAt(res.data.certUpdatedAt)
      }
      setCertFiles({ key: EMPTY_CERT_SLOT, cert: EMPTY_CERT_SLOT, ca: EMPTY_CERT_SLOT })
      setSuccess('CDPH reporting settings saved.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save CDPH configuration')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  const certLabel = certOnFile
    ? `Certificate on file${certUpdatedAt ? `, updated ${new Date(certUpdatedAt).toLocaleString()}` : ''}. Uploading a new bundle replaces it.`
    : 'No certificate on file. A complete bundle is required to enable reporting.'

  return (
    <div>
      <PageHeader
        title="CDPH Reporting"
        subtitle="Configure electronic lab reporting to CDPH SaPHIRE. Which tests report is set per test type."
        actions={
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        }
      />

      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem' }}>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => update('enabled', e.target.checked)}
              data-testid="cdph-enabled-checkbox"
            />
            Enable CDPH reporting
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
          <FormField name="cdph-environment" label="Environment">
            <select
              id="cdph-environment"
              style={input}
              value={form.environment}
              onChange={(e) => handleEnvironmentChange(e.target.value as CdphEnvironment)}
              data-testid="cdph-environment-select"
            >
              <option value="stage">Stage</option>
              <option value="prod">Production</option>
            </select>
          </FormField>
          <FormField
            name="cdph-endpoint-url"
            label="Endpoint URL"
            value={form.endpointUrl}
            onChange={(v) => update('endpointUrl', v)}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <FormField
            name="cdph-submitter-oid"
            label="Submitter OID"
            value={form.submitterOid}
            onChange={(v) => update('submitterOid', v)}
          />
          <FormField name="cdph-clia" label="CLIA" value={form.clia} onChange={(v) => update('clia', v)} />
          <FormField
            name="cdph-sending-facility"
            label="Sending Facility"
            value={form.sendingFacility}
            onChange={(v) => update('sendingFacility', v)}
          />
        </div>

        <div
          style={{
            padding: '0.75rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            background: colors.bgMuted,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>mTLS Certificate</div>
          <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '0.5rem' }}>{certLabel}</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <FormField name="cdph-cert-key" label="Client Key (PEM)">
              <input type="file" accept=".pem,.key" onChange={readCertFile('key')} data-testid="cdph-cert-key-input" />
              {certFiles.key.fileName && (
                <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                  {certFiles.key.fileName}
                </div>
              )}
            </FormField>
            <FormField name="cdph-cert-cert" label="Client Certificate (PEM)">
              <input
                type="file"
                accept=".pem,.crt,.cert"
                onChange={readCertFile('cert')}
                data-testid="cdph-cert-cert-input"
              />
              {certFiles.cert.fileName && (
                <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                  {certFiles.cert.fileName}
                </div>
              )}
            </FormField>
            <FormField name="cdph-cert-ca" label="CA Chain (PEM)">
              <input
                type="file"
                accept=".pem,.crt,.cert"
                onChange={readCertFile('ca')}
                data-testid="cdph-cert-ca-input"
              />
              {certFiles.ca.fileName && (
                <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                  {certFiles.ca.fileName}
                </div>
              )}
            </FormField>
          </div>
        </div>
      </div>

      <div style={{ ...card, marginTop: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: colors.textHeading }}>Reportable Tests</h3>
          <button
            type="button"
            onClick={() => downloadReportableCsv(reportableRows)}
            disabled={reportableRows.length === 0}
            style={{
              background: 'none',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              padding: '0.35rem 0.75rem',
              fontSize: '0.8125rem',
              cursor: reportableRows.length === 0 ? 'default' : 'pointer',
              opacity: reportableRows.length === 0 ? 0.6 : 1,
            }}
          >
            Download CSV
          </button>
        </div>

        {reportableRows.length === 0 ? (
          <div style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
            No test types are configured for CDPH reporting. Enable reporting per test under Test Types.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: `1px solid ${colors.tableBorder}` }}>
                  Test Name
                </th>
                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: `1px solid ${colors.tableBorder}` }}>
                  LOINC
                </th>
                <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: `1px solid ${colors.tableBorder}` }}>
                  Polarity
                </th>
              </tr>
            </thead>
            <tbody>
              {reportableRows.map((r, i) => (
                <tr key={`${r.name}-${i}`}>
                  <td style={{ padding: '0.4rem', borderBottom: `1px solid ${colors.tableBorder}` }}>{r.name}</td>
                  <td style={{ padding: '0.4rem', borderBottom: `1px solid ${colors.tableBorder}` }}>
                    {r.loinc || '--'}
                  </td>
                  <td style={{ padding: '0.4rem', borderBottom: `1px solid ${colors.tableBorder}` }}>
                    {r.polarity === 'all' ? 'All results' : 'Positive only'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
